/**
 * BUNKER 84 — player state + collision.
 *
 * The player is a point with a facing angle in tile-space. For
 * collision we treat them as a small circle (radius {@link PLAYER_RADIUS})
 * so they slide along walls instead of catching on corners.
 *
 * v0.1.2 ships data + math only — no input wiring yet (sc-4537 does
 * that). The collision helper is the deliverable a future test
 * harness pins behavior against.
 */

import { isSolid, tileAt } from './tiles.js';

/**
 * The player avatar. `x` and `y` are floats in tile-space; `angle`
 * is in radians with 0 pointing along +X. Negative Y is north
 * (canvas convention).
 */
export interface Player {
  x: number;
  y: number;
  angle: number;
}

/**
 * Player collision radius in tile units. 0.2 is wide enough to feel
 * solid against walls but tight enough that the 1.0-tile-wide
 * corridors in level1 stay passable at any approach angle.
 */
export const PLAYER_RADIUS = 0.2;

/**
 * Try to move `player` by `(dx, dy)` in tile-space, clamping each
 * axis independently against wall collision. Independent-axis
 * clamping is what lets the player slide along walls — if you blocked
 * both axes whenever either collided, diagonal motion would feel
 * sticky against any wall.
 *
 * Mutates `player.x` / `player.y` in place. `player.angle` is
 * untouched here (turning is a separate concern).
 *
 * **Pure function over (player, dx, dy, map)** — the only mutation
 * is the player's own position. No allocations, no globals, no
 * `Math.random()`. Safe to call from `update()` deterministically.
 */
export function moveWithCollision(
  player: Player,
  dx: number,
  dy: number,
  map: Uint8Array
): void {
  // X axis.
  if (dx !== 0) {
    const newX = player.x + dx;
    // The "leading edge" of the player's collision circle along X.
    const probeX = dx > 0 ? newX + PLAYER_RADIUS : newX - PLAYER_RADIUS;
    const tileX = Math.floor(probeX);
    // Probe at both the top and bottom of the player circle so we
    // catch walls the body is brushing, not just the centerline.
    const yTop = Math.floor(player.y - PLAYER_RADIUS);
    const yBot = Math.floor(player.y + PLAYER_RADIUS);
    const blocked =
      isSolid(tileAt(map, tileX, yTop)) ||
      isSolid(tileAt(map, tileX, yBot));
    if (!blocked) {
      player.x = newX;
    }
  }

  // Y axis. Same logic, using the (possibly updated) player.x so the
  // second probe respects the slide we just took on X.
  if (dy !== 0) {
    const newY = player.y + dy;
    const probeY = dy > 0 ? newY + PLAYER_RADIUS : newY - PLAYER_RADIUS;
    const tileY = Math.floor(probeY);
    const xLeft = Math.floor(player.x - PLAYER_RADIUS);
    const xRight = Math.floor(player.x + PLAYER_RADIUS);
    const blocked =
      isSolid(tileAt(map, xLeft, tileY)) ||
      isSolid(tileAt(map, xRight, tileY));
    if (!blocked) {
      player.y = newY;
    }
  }
}
